import { Inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

import { AnalyticsTracker } from './analytics-tracker';
import { RoutePaths } from '@helper/route-paths';
import { CartItem, OrderInfo, PageView, ProductItem } from './analytics-data';
import { Product } from '@proto/store/api/store/product';
import { WindowDetectService } from '@service/window-detect.service';
import { EventRepositoryService } from '@service/event-repository.service';
import { AddFbEventRequest } from '@proto/store/api/store/misc/event_record/event_record';
import { Customer_Provider } from '@proto/api/customers/resources';
import { PreferenceKey } from '@helper/preference-key';
import { environment } from '../../environments/environment';

import { getAnalytics, isSupported, logEvent } from 'firebase/analytics';
import { Analytics } from '@firebase/analytics';
import firebase from 'firebase/compat/app';
import 'firebase/compat/analytics';
import { v4 as uuid } from 'uuid';

declare const fbq: any;
declare const gtag: any;
declare const _tfa: any;

@Injectable({
  providedIn: 'root',
})
export class EventTracker implements AnalyticsTracker {
  readonly currency = 'TWD';
  readonly track = 'track';
  readonly product = 'product';
  readonly productGroup = 'product_group';
  readonly taboolaId = '1780411';

  // 商品頁 tracker 需要知道是從哪個 path 跳轉過來的, 使用此變數儲存上一個 path
  private pageView: PageView | undefined = undefined;
  private analytics?: Analytics;
  private renderer?: Renderer2;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    windowService: WindowDetectService,
    private eventRepository: EventRepositoryService,
    @Inject(DOCUMENT) private document: Document,
    rendererFactory: RendererFactory2
  ) {
    if (
      isPlatformBrowser(this.platformId) &&
      environment.production &&
      !windowService.isWebsiteForApp() &&
      !windowService.isLegacyWebsite() &&
      !windowService.isPreviewWebsite()
    ) {
      isSupported().then(supported => {
        if (supported) {
          this.analytics = getAnalytics();
          this.renderer = rendererFactory.createRenderer(null, null);
          this.metaPixelInit();
          this.bingSearchInit();
          this.taboolaInit();
        }
      });
    }
  }

  onLogin(type: string): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.LOGIN, { source: type });
    const eventID = uuid();
    fbq(this.track, 'CompleteRegistration', { currency: this.currency }, { eventID });
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'CompleteRegistration',
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  screenView(url: string, lastPageView: PageView | undefined): PageView | undefined {
    if (this.analytics && typeof _tfa !== 'undefined') {
      _tfa.push({
        notify: 'event',
        name: 'page_view',
        'item-url': `${environment.endPointUrl}${url}`,
        id: this.taboolaId,
      });
    }
    const pageView = this.getPageViewFromPath(url);
    if (!this.analytics || !pageView || (pageView === lastPageView && pageView !== PageView.MINI_HOME)) {
      return pageView;
    }
    logEvent(this.analytics, firebase.analytics.EventName.SCREEN_VIEW, {
      firebase_screen: pageView.valueOf(),
      firebase_screen_class: pageView.valueOf(),
    });
    return pageView;
  }

  onPromotion(id: string, name: string): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.VIEW_PROMOTION, {
      promotion_id: id,
      promotion_name: name,
    });
  }

  onProduct(data: ProductItem, productId: string, page: PageView): void {
    if (!this.analytics) {
      return;
    }
    const price = Number(data.price || 0);
    if (data.promotionId) {
      logEvent(this.analytics, firebase.analytics.EventName.SELECT_PROMOTION, {
        promotion_id: data.promotionId,
        promotion_name: data.promotionName,
        source: page.valueOf(),
        items: [
          {
            item_id: data.id,
            item_name: data.name,
            item_category: data.category,
            price,
          },
        ],
      });
    }
    logEvent(this.analytics, firebase.analytics.EventName.VIEW_ITEM, {
      currency: this.currency,
      source: page.valueOf(),
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          item_category: data.category,
          price,
        },
      ],
    });
    const groupEventID = uuid();
    fbq(
      this.track,
      'ViewContent',
      {
        content_ids: [productId],
        content_type: this.productGroup,
        currency: this.currency,
      },
      { groupEventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: groupEventID,
          eventName: 'ViewContent',
          contentIds: [productId],
          contentType: this.productGroup,
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
    const eventID = uuid();
    fbq(
      this.track,
      'ViewContent',
      {
        content_ids: [data.id],
        content_type: this.product,
        currency: this.currency,
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'ViewContent',
          contentIds: [data.id],
          contentType: this.product,
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  onAddToShoppingCart(data: ProductItem): void {
    if (!this.analytics) {
      return;
    }
    const price = Number(data.price || 0);
    logEvent(this.analytics, firebase.analytics.EventName.ADD_TO_CART, {
      currency: this.currency,
      value: price * data.quantity,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          price,
          quantity: data.quantity,
        },
      ],
    });
    const eventID = uuid();
    fbq(
      this.track,
      'AddToCart',
      {
        value: price * data.quantity,
        content_type: this.product,
        currency: this.currency,
        contents: [{ id: data.id, quantity: data.quantity, item_price: data.price, name: data.name }],
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'AddToCart',
          contentType: this.product,
          contentIds: [data.id],
          value: (price * data.quantity).toString(),
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  onRemoveFromShoppingCart(data: ProductItem): void {
    if (!this.analytics) {
      return;
    }
    const price = Number(data.price || 0);
    logEvent(this.analytics, firebase.analytics.EventName.REMOVE_FROM_CART, {
      currency: this.currency,
      value: price * data.quantity,
      items: [
        {
          item_id: data.id,
          item_name: data.name,
          price,
          quantity: data.quantity,
        },
      ],
    });
  }

  onShoppingCart(items: ProductItem[]): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.VIEW_CART, {
      currency: this.currency,
      items: this.getItems(items),
    });
  }

  onCheckout(items: ProductItem[], totalPrice: number): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.BEGIN_CHECKOUT, {
      currency: this.currency,
      value: totalPrice,
      items: this.getItems(items),
    });
    const eventID = uuid();
    fbq(
      this.track,
      'InitiateCheckout',
      {
        value: totalPrice,
        currency: this.currency,
        num_items: items.reduce((prev, curr) => prev + curr.quantity, 0),
        content_type: this.product,
        contents: items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          item_price: item.price,
          name: item.name,
        })),
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'InitiateCheckout',
          contentType: this.product,
          contentIds: items.map(item => item.id),
          value: totalPrice.toString(),
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  onShipping(items: ProductItem[], totalPrice: number, type: string): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.ADD_SHIPPING_INFO, {
      currency: this.currency,
      value: totalPrice,
      shipping_tier: type,
      items: this.getItems(items),
    });
    const eventID = uuid();
    fbq(
      this.track,
      'AddPaymentInfo',
      {
        currency: this.currency,
        contents: items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          item_price: item.price,
          name: item.name,
        })),
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'AddPaymentInfo',
          contentIds: items.map(item => item.id),
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  onPaymentCompleted(orderInfo: OrderInfo): void {
    if (!this.analytics) {
      return;
    }
    if (typeof _tfa !== 'undefined') {
      _tfa.push({ notify: 'event', name: 'make_purchase', id: this.taboolaId });
    }
    logEvent(this.analytics, firebase.analytics.EventName.PURCHASE, {
      transaction_id: orderInfo.id,
      affiliation: 'lativ',
      currency: this.currency,
      value: Number(orderInfo.total || 0),
      shipping: Number(orderInfo.shipping || 0),
      items: orderInfo.items.map((item: ProductItem, index: number) => ({
        index,
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: Number(item.price || 0),
      })),
    });
    gtag('event', 'conversion', {
      send_to: 'AW-1030505086/6ePYCKKmxwEQ_oSx6wM',
      transaction_id: orderInfo.id,
      value: 0,
      currency: 'USD',
      dynx_itemid: orderInfo.items.map((item: ProductItem) => item.id),
    });
    const eventID = uuid();
    fbq(
      this.track,
      'Purchase',
      {
        value: Number(orderInfo.total || 0),
        currency: this.currency,
        num_items: orderInfo.items.reduce((prev, curr) => prev + curr.quantity, 0),
        content_type: this.product,
        contents: orderInfo.items.map(item => ({
          id: item.id,
          quantity: item.quantity,
          item_price: item.price,
          name: item.name,
        })),
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'Purchase',
          contentType: this.product,
          contentIds: orderInfo.items.map(item => item.id),
          value: orderInfo.total,
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  // 加入最愛
  onFavorite(product: Product): void {
    if (!this.analytics) {
      return;
    }
    const price = Number(product.meta?.promotionPrice || product.meta?.originPrice || 0);
    logEvent(this.analytics, firebase.analytics.EventName.ADD_TO_WISHLIST, {
      currency: this.currency,
      value: price,
      items: [
        {
          item_id: product.meta?.productId,
          item_name: product.meta?.name,
          price,
          quantity: 1,
        },
      ],
    });
    const eventID = uuid();
    fbq(
      this.track,
      'AddToWishlist',
      {
        value: price,
        content_type: this.product,
        currency: this.currency,
        contents: [{ id: product.meta?.productId, quantity: 1, item_price: price, name: product.meta?.name }],
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'AddToWishlist',
          contentType: this.product,
          contentIds: [product.meta?.productId || ''],
          value: price.toString(),
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  // 搜尋欄位時
  onSearch(keyword: string, success: boolean): void {
    if (!this.analytics) {
      return;
    }
    logEvent(this.analytics, firebase.analytics.EventName.SEARCH, {
      search_term: keyword,
      success: success ? '1' : '0',
    });
    const eventID = uuid();
    fbq(
      this.track,
      'Search',
      {
        content_type: this.productGroup,
        currency: this.currency,
        search_string: keyword,
        content_ids: [keyword],
      },
      { eventID }
    );
    this.eventRepository
      .addFbEvent(
        AddFbEventRequest.fromPartial({
          id: eventID,
          eventName: 'Search',
          contentType: this.productGroup,
          contentIds: [keyword],
          sourceUrl: this.document.location.href,
          provider: this.getLoginType() || Customer_Provider.PROVIDER_PHONE,
        })
      )
      .subscribe();
  }

  setPageView(path?: string): void {
    this.pageView = path ? this.getPageViewFromPath(path) : undefined;
  }

  getPageView(): PageView | undefined {
    return this.pageView;
  }

  private getItems(items: ProductItem[]): CartItem[] {
    return items.map(
      (item, index) => new CartItem(index, item.id, item.name, item.category, item.quantity, Number(item.price || 0))
    );
  }

  private getPageViewFromPath(path: string): PageView | undefined {
    const value = path.slice(1);
    const size = value.split('/').length;
    if (!value) {
      return PageView.HOME;
    } else if (value.startsWith(RoutePaths.HOME) && size === 2) {
      return PageView.MINI_HOME;
    } else if (
      (value.startsWith(`${RoutePaths.HOME}/${RoutePaths.SALES_EVENT}`) && size === 4) ||
      (value.startsWith(RoutePaths.SALES_EVENT) && size === 3)
    ) {
      return PageView.SALES_EVENT;
    } else if (value.startsWith(RoutePaths.PRODUCT)) {
      return PageView.PRODUCT;
    } else if (value.startsWith(RoutePaths.CATEGORY) && size === 3) {
      return PageView.CATEGORY;
    } else if (value.startsWith(RoutePaths.CATEGORY) && size === 4) {
      return PageView.CATEGORY_LIST;
    } else if (value.startsWith(RoutePaths.OUTFIT) && size === 1) {
      return PageView.OUTFIT;
    } else if (value.startsWith(RoutePaths.OUTFIT) && size === 2) {
      return PageView.OUTFIT_GRID;
    } else if (value.startsWith(RoutePaths.OUTFIT) && size === 3) {
      return PageView.OUTFIT_DETAIL;
    } else if (value === `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}`) {
      return PageView.NOTIFICATION;
    } else if (value === `${RoutePaths.USER}/${RoutePaths.NOTIFICATION}/${RoutePaths.PROMOTION}`) {
      return PageView.NOTIFICATION_PROMOTIONS;
    } else if (value.startsWith(RoutePaths.CART) && size === 1) {
      return PageView.CART;
    } else if (value.startsWith(`${RoutePaths.CART}/${RoutePaths.SHIPPING}`)) {
      return PageView.SHIPPING;
    } else if (value.startsWith(`${RoutePaths.CART}/${RoutePaths.PAYMENT}`) && size === 2) {
      return PageView.PAYMENT;
    } else if (value.startsWith(`${RoutePaths.CART}/${RoutePaths.PAYMENT}`) && size === 3) {
      return PageView.PAYMENT_RESULT;
    } else if (value === `${RoutePaths.USER}/${RoutePaths.FAVORITE}`) {
      return PageView.FAVORITE;
    } else if (value === `${RoutePaths.USER}/${RoutePaths.REMINDER}`) {
      return PageView.ARRIVAL;
    } else if (value.startsWith(RoutePaths.SHOP_WINDOW) && size === 2) {
      return PageView.STAGGERED_SHOP_WINDOW;
    } else if (value.startsWith(RoutePaths.SPECIAL) && size === 2) {
      return PageView.SPECIAL;
    } else if (value.startsWith(RoutePaths.SEARCH) && size === 1) {
      return PageView.SEARCH;
    } else if (value.startsWith(RoutePaths.SEARCH) && size > 1) {
      return PageView.SEARCH_RESULT;
    } else if (value.startsWith(RoutePaths.SUPPORT_SERVICE) && size === 1) {
      return PageView.CUSTOMER_SERVICE;
    } else if (value.startsWith(RoutePaths.CUSTOMER_SERVICE) && size === 1) {
      return PageView.ONLINE_SERVICE;
    } else if (value.startsWith(`${RoutePaths.ORDERS}/${RoutePaths.ORDER_DETAIL}`) && size === 3) {
      return PageView.ORDER_DETAIL;
    } else if (value.startsWith(RoutePaths.COMMENTS)) {
      return PageView.RATING;
    } else if (value.startsWith(`${RoutePaths.ORDERS}/${RoutePaths.RECEIPT_DETAIL}`)) {
      return PageView.RECEIPT;
    } else {
      return undefined;
    }
  }

  private metaPixelInit(): void {
    if (!this.renderer) {
      return;
    }
    const scriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(scriptElement, 'id', 'pixel-script');
    this.renderer.setAttribute(scriptElement, 'type', 'text/javascript');
    this.renderer.setProperty(
      scriptElement,
      'innerHTML',
      `var pixelCode = function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '567690756667739');fbq('track', 'PageView');`
    );
    this.renderer.appendChild(document.head, scriptElement);
  }

  private bingSearchInit(): void {
    if (!this.renderer) {
      return;
    }
    const scriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(scriptElement, 'id', 'bing-script');
    this.renderer.setAttribute(scriptElement, 'type', 'text/javascript');
    this.renderer.setProperty(
      scriptElement,
      'innerHTML',
      `!function(a,e,n,t,o){var c,r,d;a[o]=a[o]||[],c=function(){var e={ti:"271001748",enableAutoSpaTracking:!0};e.q=a[o];if(a.UET){a[o]=new UET(e)||[]};a[o].push("pageLoad")},(r=e.createElement(n)).src="//bat.bing.com/bat.js",r.async=1,r.onload=r.onreadystatechange=function(){var e=this.readyState;e&&"loaded"!==e&&"complete"!==e||(c(),r.onload=r.onreadystatechange=null)},(d=e.getElementsByTagName(n)[0]).parentNode.insertBefore(r,d)}(window,document,"script",0,"uetq");`
    );
    this.renderer.appendChild(document.head, scriptElement);
  }

  private taboolaInit(): void {
    if (!this.renderer) {
      return;
    }
    const scriptElement = this.renderer.createElement('script');
    this.renderer.setAttribute(scriptElement, 'id', 'taboola-script');
    this.renderer.setAttribute(scriptElement, 'type', 'text/javascript');
    this.renderer.setProperty(
      scriptElement,
      'innerHTML',
      `window._tfa = window._tfa || []; window._tfa.push({notify: 'event', name: 'page_view', id: 1780411}); !function (t, f, a, x) { if (!document.getElementById(x)) { t.async = 1;t.src = a;t.id=x;f.parentNode.insertBefore(t, f); } }(document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/unip/1780411/tfa.js', 'tb_tfa_script');`
    );
    this.renderer.appendChild(document.head, scriptElement);
  }

  private getLoginType(): Customer_Provider | undefined {
    let data: string | undefined = Customer_Provider.PROVIDER_PHONE.toString();
    try {
      data = localStorage?.getItem(PreferenceKey.LOGIN_TYPE) ?? undefined;
    } catch {
      // ignore
    }
    if (!data) {
      return undefined;
    }
    const type = Number(data);
    if (!isNaN(type) && Object.values(Customer_Provider).includes(type)) {
      return type;
    }
    return undefined;
  }
}
